// ===========================================
// Retro-Edit Report (2026-09-18) — the eleventh report, and a SUPERVISION one
// ===========================================
// Who corrected the past, how late, and how often.
//
// ⚠️ THE HEADLINE IS A TABLE OF PEOPLE, WHICH INVERTS EVERY OTHER REPORT HERE.
// The owner's question is "are staff repeatedly using the retro-edit feature to
// cover work they didn't do on time?" (Andrew, 2026-09-17), so `byActor` leads
// and the row log below it is the drill-down, not the point.
//
// ⚠️ THIS PAGE NAMES PEOPLE IN A CRITICAL LIGHT. Three consequences for how it
// reads: a high count is shown beside its CONTROL (total edits vs the
// retroactive subset), because "12 edits, 1 late" and "12 edits, 12 late" are
// opposite findings a single number would conflate; the lag figures are
// medians, never means, so one ancient correction cannot brand an otherwise
// punctual admin; and selecting a person FILTERS rather than accuses — the row
// log is where a claim gets checked against what actually happened.
//
// ⚠️ TWO DATE KINDS, TWO FORMATTERS, AND MIXING THEM HAS ALREADY SHIPPED A BUG
// IN THIS FEATURE FAMILY (QA 2026-08-18: `recordedAt` rendered as YESTERDAY for
// an admin acting before ~10am Sydney).
//   • `editedDate` is a `@db.Date` — a calendar day stored at UTC midnight.
//     Read it with `formatUTCDate` (UTC getters), never date-fns `format()`.
//   • `performedAt` is a real INSTANT. Read it with `formatInSchoolTz` in the
//     school's zone, never the browser's — the whole report is about WHEN
//     someone acted relative to the school's day.
//
// Three states are rendered distinctly — loading, error, empty. An empty table
// must never read as "nobody edits retroactively" when it might mean the report
// is broken (CLAUDE.md: an empty list is not a fact).

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  WarningAmber as WarningAmberIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  FilterAltOff as ClearFilterIcon,
} from '@mui/icons-material';
import { useRetroEditsReport, useExportRetroEditsReport } from '../../../hooks/useReports';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import ExportButtons from '../../../components/reports/ExportButtons';
import { formatUTCDate, formatInSchoolTz } from '../../../utils/dateUtils';
import type { RetroEditsReportFilters, RetroEditRow } from '../../../api/reports.api';

/**
 * ⚠️ LOCAL getters, deliberately. This builds a key for a SCHOOL-LOCAL day
 * question ("the last 30 days") from the browser's own clock, matching
 * `defaultEmailLogFilters`. The backend re-resolves the key in the school's
 * zone, so the round-trip is correct; using UTC getters here would ask for the
 * wrong day for anyone west of UTC.
 */
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Default period: the last 30 days, so the page shows data on arrival. */
export function defaultRetroEditsFilters(now = new Date()): RetroEditsReportFilters {
  const start = new Date(now);
  start.setDate(start.getDate() - 29);
  return { startDate: dayKey(start), endDate: dayKey(now), page: 1, pageSize: 25 };
}

/** A week late is the point past which "recorded near the time" plainly failed. */
const LATE_THRESHOLD_DAYS = 7;

/**
 * Human labels for every action, as an EXPLICIT MAP.
 *
 * ⚠️ Not a `replace(/_/g, ' ')` like `SecurityAuditPage`, and not a substring
 * test. On a supervision surface "what did this person actually do?" is a
 * product question: a future action must be labelled deliberately rather than
 * rendered as whatever its constant happens to spell. An unknown value falls
 * back to the raw string (the column is a String precisely so a new
 * retro-capable path costs no migration), so the log can always SHOW a value it
 * cannot yet name.
 */
const ACTION_LABELS: Record<string, string> = {
  REPLACEMENT_TEACHER_ASSIGNED: 'Replacement teacher assigned',
  OCCURRENCE_ROOM_CHANGED: 'Room changed',
  OCCURRENCE_RESCHEDULED: 'Occurrence rescheduled',
  CLINIC_PRE_BOOKED: 'Clinic pre-booked',
  CLINIC_RESCHEDULED: 'Clinic rescheduled',
  CLINIC_ROOM_CHANGED: 'Clinic room changed',
  CLINIC_TEACHER_CHANGED: 'Clinic teacher changed',
  EXCEPTION_DELETED: 'Exception deleted',
  REPLACEMENT_WEEKS_CLEARED: 'Replacement weeks cleared',
  REPLACEMENT_TEACHER_CLEARED: 'Replacement teacher cleared',
  EXTRA_LESSON_BOOKED: 'Extra lesson recorded',
  EXTRA_LESSON_RESCHEDULED: 'Extra lesson moved',
  ENROLLMENT_START_WEEK_CHANGED: 'Start week changed',
  ATTENDANCE_CHANGED: 'Attendance changed',
  ATTENDANCE_MARKED_LATE: 'Attendance marked late',
  ATTENDANCE_OVERWRITTEN_BY_CANCEL: 'Attendance overwritten by cancellation',
  TERM_REOPENED: 'Term reopened',
  TERM_RELOCKED: 'Term locked again',
  TERM_DATES_CHANGED: 'Term dates changed',
  LOCKED_TERM_CLASS_CHANGED: 'Locked-term class changed',
};

/**
 * What KIND of record an audit row is about. Six values today; an unknown one
 * falls back to the raw string for the same reason `actionLabel` does — the
 * column is a String so a new retro-capable path costs no migration, and the
 * log must always SHOW a value it cannot yet name.
 */
const ENTITY_LABELS: Record<string, string> = {
  LESSON_OCCURRENCE: 'Class',
  HYBRID_BOOKING: 'Clinic booking',
  EXTRA_LESSON: 'Extra lesson',
  ENROLLMENT: 'Enrolment',
  ATTENDANCE: 'Attendance',
  TERM: 'Term',
};

/**
 * The actions that ERASE a record rather than change one — mirrors
 * `DELETION_ACTIONS` in `retro-edits.report.ts`. Kept as a list rather than a
 * name test for the same reason: it is a product classification, not a spelling
 * convention.
 */
const DELETION_ACTIONS = new Set([
  // #42: ATTENDANCE_OVERWRITTEN_BY_CANCEL is deliberately NOT here — the row survives as the
  // make-up anchor and `details.before` keeps the erased mark; it is an edit, not an erasure.
  'EXCEPTION_DELETED',
  'REPLACEMENT_WEEKS_CLEARED',
  'REPLACEMENT_TEACHER_CLEARED',
]);

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function ActionChip({ action }: { action: string }) {
  // A deletion is the sharper event, so it reads as such. Everything else is
  // neutral: a retroactive edit is not by itself wrongdoing, and colouring all
  // of them as warnings would make the genuinely late ones invisible.
  const isDeletion = DELETION_ACTIONS.has(action);
  return (
    <Chip
      label={actionLabel(action)}
      size="small"
      color={isDeletion ? 'error' : 'default'}
      variant={isDeletion ? 'filled' : 'outlined'}
    />
  );
}

/**
 * The lag chip — THE metric of this report.
 *
 * ⚠️ A NEGATIVE lag is legitimate and must not read as an error: it means the
 * edit was made BEFORE the class day (an ordinary forward edit), which is the
 * control group that makes "records late" mean anything. Zero is same-day.
 */
export function lagDescription(lagDays: number): {
  label: string;
  color: 'default' | 'warning' | 'error';
} {
  if (lagDays < 0) return { label: `${Math.abs(lagDays)}d early`, color: 'default' };
  if (lagDays === 0) return { label: 'Same day', color: 'default' };
  if (lagDays <= LATE_THRESHOLD_DAYS) return { label: `${lagDays}d late`, color: 'warning' };
  return { label: `${lagDays}d late`, color: 'error' };
}

function LagChip({ lagDays }: { lagDays: number }) {
  const { label, color } = lagDescription(lagDays);
  return <Chip label={label} size="small" color={color} />;
}

/** The expandable `details` blob — `{ before, after }` plus any degradation flags. */
function DetailsCell({ details }: { details: unknown }) {
  const [open, setOpen] = useState(false);
  const hasDetails =
    details !== null &&
    details !== undefined &&
    typeof details === 'object' &&
    Object.keys(details as object).length > 0;

  if (!hasDetails) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box>
      <IconButton
        size="small"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Hide details' : 'Show details'}
      >
        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </IconButton>
      <Collapse in={open} unmountOnExit>
        <Paper variant="outlined" sx={{ mt: 1, p: 1, bgcolor: 'background.default' }}>
          <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
            {JSON.stringify(details, null, 2)}
          </pre>
        </Paper>
      </Collapse>
    </Box>
  );
}

function RetroEditLogRow({ row, timeZone }: { row: RetroEditRow; timeZone: string }) {
  return (
    <TableRow hover>
      <TableCell>
        {/* ⚠️ An INSTANT in the SCHOOL's zone — not the browser's. This report
            is about when someone acted relative to the school's day. */}
        <Typography variant="body2">
          {formatInSchoolTz(row.performedAt, timeZone, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">{row.performedByName}</Typography>
          {row.performedById === null && (
            <Tooltip title="This account no longer exists; the name is the one recorded at the time.">
              <Chip label="Former user" size="small" variant="outlined" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <ActionChip action={row.action} />
      </TableCell>
      <TableCell>
        {/* ⚠️ WHICH RECORD was changed. Without this the log says Sarah made
            eight late edits but never which classes — and "check the claim
            against what actually happened" is the entire purpose of this log,
            so an unattributable row cannot be checked at all. (Dropped from
            Phase 7, caught by its /qa; the plan specified an entity column.)
            Rendered as a LABEL + copyable id rather than a link: the two entity
            types point at different admin surfaces and inventing a route that
            404s would be worse than showing the identifier plainly. */}
        <Tooltip title={row.entityId}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
            {ENTITY_LABELS[row.entityType] ?? row.entityType}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {row.entityId.slice(-6)}
            </Typography>
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {/* ⚠️ A @db.Date — UTC getters, dd/mm/yyyy en-AU (CLAUDE.md rule 7).
            date-fns `format()` here renders the previous day west of UTC. */}
        <Typography variant="body2">
          {formatUTCDate(
            row.editedDate,
            { day: '2-digit', month: '2-digit', year: 'numeric' },
            'en-AU'
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <LagChip lagDays={row.lagDays} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={row.reason ? undefined : 'text.secondary'}>
          {row.reason || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <DetailsCell details={row.details} />
      </TableCell>
    </TableRow>
  );
}

/**
 * The report audits the staff, so it is the owner's alone (roles slice 2, V25): the server refuses
 * anyone but an Admin, the nav item and the route guard hide it, and this page asks nothing of the
 * server for anyone else (a deep link, or a guard that missed it, shows this instead of a 403).
 */
export default function RetroEditsReportPage() {
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  if (!isOwner) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Only your {ownerTitle} can open the Retro Edits report.</Alert>
      </Box>
    );
  }
  return <RetroEditsReport />;
}

function RetroEditsReport() {
  const [filters, setFilters] = useState<RetroEditsReportFilters>(() => defaultRetroEditsFilters());
  const { data, isLoading, isError, error } = useRetroEditsReport(filters);
  const exportMutation = useExportRetroEditsReport();

  const summary = data?.summary;
  // ⚠️ `Array.isArray`, never `!data` — a wrapper-vs-array mismatch would
  // otherwise pass an object straight into `.map` and crash the page.
  const byActor = Array.isArray(data?.byActor) ? data.byActor : [];
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const timeZone = summary?.timeZone ?? 'Australia/Sydney';

  const actionOptions = useMemo(
    () => Object.keys(ACTION_LABELS).map((a) => ({ value: a, label: ACTION_LABELS[a] })),
    []
  );
  // #42: attendance entries will be most of the log, so the owner needs to separate them
  // from class-schedule edits. The server already filters by entityType (summary AND rows).
  const entityOptions = useMemo(
    () => Object.keys(ENTITY_LABELS).map((e) => ({ value: e, label: ENTITY_LABELS[e] })),
    []
  );

  // Any filter change resets to page 1: staying on page 4 of a narrower result
  // set shows an empty table that reads as "nothing found".
  const patch = (next: Partial<RetroEditsReportFilters>) =>
    setFilters((f) => ({ ...f, ...next, page: 1 }));

  const activeActorName = byActor.find(
    (a) => a.performedById === filters.performedById
  )?.performedByName;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Retro Edits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Changes made to past classes — who made them, and how long after the class they were
            recorded.
          </Typography>
        </Box>
        {/* ⚠️ The export carries the CURRENT filters, so what downloads matches
            what is on screen. CSV is every row in the period; PDF is the
            summary + the per-person table. Disabled while there is nothing to
            export, so a click cannot produce an empty file that reads as
            "no one retro-edits". */}
        <ExportButtons
          onExport={(format) => exportMutation.mutate({ format, filters })}
          loading={exportMutation.isPending}
          disabled={!data || data.pagination.total === 0}
        />
      </Stack>

      {/* ⚠️ Loading, error and empty are three DIFFERENT states. React Query
          leaves `data` undefined while loading AND on error, so `?? []` alone
          would collapse both into a misleading "no retro edits". */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          Could not load the retro-edit report
          {error instanceof Error ? `: ${error.message}` : '.'} The audit log itself is unaffected —
          this is a display failure, not missing data.
        </Alert>
      ) : (
        <>
          {/* ⚠️ THE TIMEZONE DISCLOSURE, keyed on `timeZoneSource` and NEVER on
              `timeZone`. The first cut of this banner tested `timeZone === 'UTC'`
              and was wrong in BOTH directions (measured, Phase 6 /qa): a school
              with NO timezone is masked as the product default before the
              backend's guard sees it, so the broken case showed nothing; while
              'UTC' is a legitimate IANA zone, so a school genuinely running on
              UTC was told its settings were broken. The string cannot carry the
              distinction — the backend now sends how it resolved. */}
          {summary?.timeZoneSource === 'missing' && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
              This school has <strong>no timezone configured</strong>, so the period was cut using
              the default (<strong>{summary.timeZone}</strong>). Day boundaries — and therefore how
              late each edit appears — may be off by up to a day. Set the timezone in school
              settings.
            </Alert>
          )}
          {summary?.timeZoneSource === 'invalid' && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
              This school's timezone (<strong>{summary.timeZoneStored}</strong>) is not a zone this
              system recognises, so the period was cut using the default (
              <strong>{summary.timeZone}</strong>). Day boundaries may be off by up to a day. Correct
              it in school settings.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Retroactive Edits', value: summary?.retroactiveEdits ?? 0 },
              { label: 'Total Edits', value: summary?.totalEdits ?? 0 },
              { label: 'Deletions', value: summary?.totalDeletions ?? 0 },
              { label: 'Median Lag (days)', value: summary?.medianLagDays ?? 0 },
              {
                label: 'Worst Lag (days)',
                value: summary?.worstLagDays ?? 0,
                alert: (summary?.worstLagDays ?? 0) > LATE_THRESHOLD_DAYS,
              },
            ].map((stat) => (
              // xs={12}: at 412px two cards per row wrap "Median Lag (days)"
              // onto three lines at uneven heights. height:100% keeps a row level.
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={stat.label}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" sx={{ color: stat.alert ? 'error.main' : undefined }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ---- Filters ---- */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* Five md=3 fields: a 15-column row keeps them on one line on desktop (#42 Record type). */}
              <Grid container spacing={2} alignItems="center" columns={{ xs: 12, sm: 12, md: 15 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDate ?? ''}
                    onChange={(e) => patch({ startDate: e.target.value || undefined })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={filters.endDate ?? ''}
                    onChange={(e) => patch({ endDate: e.target.value || undefined })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Action"
                    value={filters.action ?? ''}
                    onChange={(e) => patch({ action: e.target.value || undefined })}
                  >
                    <MenuItem value="">All actions</MenuItem>
                    {actionOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Record type"
                    value={filters.entityType ?? ''}
                    onChange={(e) => patch({ entityType: e.target.value || undefined })}
                    inputProps={{ 'data-testid': 'retro-edits-entity-filter' }}
                  >
                    <MenuItem value="">All records</MenuItem>
                    {entityOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {/* The owner's sharpest filter: "everything recorded more
                      than N days late". */}
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Recorded at least"
                    value={filters.minLagDays ?? ''}
                    onChange={(e) =>
                      patch({
                        minLagDays: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                  >
                    <MenuItem value="">Any lag</MenuItem>
                    <MenuItem value={1}>1+ day late</MenuItem>
                    <MenuItem value={3}>3+ days late</MenuItem>
                    <MenuItem value={7}>7+ days late</MenuItem>
                    <MenuItem value={14}>14+ days late</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {filters.performedById && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                  <Chip
                    label={`Showing only: ${activeActorName ?? 'selected person'}`}
                    onDelete={() => patch({ performedById: undefined })}
                    color="primary"
                  />
                  <Button
                    size="small"
                    startIcon={<ClearFilterIcon />}
                    onClick={() => patch({ performedById: undefined })}
                  >
                    Show everyone
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* ---- TIER 1: the per-person leaderboard (the headline) ---- */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6">By person</Typography>
                <Typography variant="body2" color="text.secondary">
                  {`Times in ${timeZone}`}
                </Typography>
              </Stack>
              {/* ⚠️ The control group, stated in words. "Retroactive" is a
                  SUBSET of the edits beside it — without that framing a busy
                  admin and a chronically late one look identical. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                "Retroactive" counts the edits recorded after the class day — a subset of that
                person's total. Lag is shown as a median so a single old correction does not skew
                it. Select a person to see their individual edits below.
              </Typography>

              {byActor.length === 0 ? (
                <Alert severity="info">
                  No retro edits were recorded in this period. (This is a real result for the
                  period, not a loading state.)
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Person</TableCell>
                        <TableCell align="right">Edits</TableCell>
                        <TableCell align="right">Deletions</TableCell>
                        <TableCell align="right">Retroactive</TableCell>
                        <TableCell align="right">Median lag</TableCell>
                        <TableCell align="right">Worst lag</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byActor.map((actor) => (
                        <TableRow
                          key={actor.performedById ?? `name:${actor.performedByName}`}
                          hover
                          selected={
                            Boolean(actor.performedById) &&
                            actor.performedById === filters.performedById
                          }
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <span>{actor.performedByName}</span>
                              {/* A null id means the account was hard-deleted;
                                  the name survives as a snapshot. Saying so
                                  stops it reading as a data error. */}
                              {actor.performedById === null && (
                                <Tooltip title="This account no longer exists; the name is the one recorded at the time.">
                                  <Chip label="Former user" size="small" variant="outlined" />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{actor.editCount}</TableCell>
                          <TableCell align="right">{actor.deletionCount}</TableCell>
                          <TableCell align="right">{actor.retroactiveCount}</TableCell>
                          <TableCell align="right">{actor.medianLagDays}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color:
                                actor.worstLagDays > LATE_THRESHOLD_DAYS ? 'error.main' : undefined,
                            }}
                          >
                            {actor.worstLagDays}
                          </TableCell>
                          <TableCell align="right">
                            {/* ⚠️ Only offered for an actor we can FILTER on. A
                                departed admin has no id and the backend filter
                                keys on the id, so an enabled button would
                                silently return nothing — worse than none. */}
                            {actor.performedById !== null ? (
                              <Button
                                size="small"
                                onClick={() =>
                                  patch({ performedById: actor.performedById ?? undefined })
                                }
                              >
                                View edits
                              </Button>
                            ) : (
                              <Tooltip title="This account no longer exists, so their edits cannot be filtered individually.">
                                <span>
                                  <Button size="small" disabled>
                                    View edits
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* ---- TIER 2: the row log (the drill-down) ---- */}
          <Card>
            <CardContent>
              {/* ⚠️ The heading NAMES the scope. The tiles and the per-person
                  table above are period-wide; this log is the one thing the
                  actor filter narrows, so an unqualified "Individual edits"
                  over a one-person list would misdescribe what is on screen. */}
              <Typography variant="h6" sx={{ mb: 1 }}>
                {filters.performedById
                  ? `Individual edits — ${activeActorName ?? 'selected person'}`
                  : 'Individual edits'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every recorded change, newest first. "Recorded" is when the edit was made; "class
                day" is the day it changed.
                {filters.performedById
                  ? ' The summary and per-person table above still cover everyone, so this person can be compared against them.'
                  : ''}
              </Typography>

              {rows.length === 0 ? (
                <Alert severity="info">
                  No individual edits match these filters. (A real result, not a loading state.)
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Recorded</TableCell>
                        <TableCell>Person</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Record</TableCell>
                        <TableCell>Class day</TableCell>
                        <TableCell>Lag</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <RetroEditLogRow key={row.id} row={row} timeZone={timeZone} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ⚠️ SERVER pagination — `page` is 1-based on the wire and
                  0-based in MUI, so it is converted on both edges. */}
              <TablePagination
                component="div"
                count={data?.pagination.total ?? 0}
                page={Math.max(0, (filters.page ?? 1) - 1)}
                onPageChange={(_e, newPage) => setFilters((f) => ({ ...f, page: newPage + 1 }))}
                rowsPerPage={filters.pageSize ?? 25}
                onRowsPerPageChange={(e) =>
                  setFilters((f) => ({ ...f, pageSize: parseInt(e.target.value, 10), page: 1 }))
                }
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
